import { AlertCircle, CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { ActionButton } from '@/components/ui+/ActionButton';

const TIMEOUT_MS = 60_000;

export const WorldlinePaymentCallback = ({
  paymentGatewayPaymentId,
  paymentRecorded,
  onSuccess,
  onBack,
}: {
  paymentGatewayPaymentId: string | null;
  paymentRecorded: boolean;
  onSuccess: () => void;
  onBack: () => void;
}) => {
  const [status, setStatus] = useState<'loading' | 'success' | 'timeout' | 'failed'>('loading');
  const timeoutRef = useRef<number | null>(null);
  const successRedirectRef = useRef<number | null>(null);

  // The wrapper decides when the backend has recorded the redirected payment.
  useEffect(() => {
    if (paymentRecorded && paymentGatewayPaymentId) {
      if (successRedirectRef.current != null) {
        window.clearTimeout(successRedirectRef.current);
      }

      setStatus('success');
      successRedirectRef.current = window.setTimeout(() => {
        onSuccess();
      }, 2000);
    }
  }, [onSuccess, paymentGatewayPaymentId, paymentRecorded]);

  // Timeout fallback
  useEffect(() => {
    if (!paymentGatewayPaymentId) {
      setStatus('failed');
      return;
    }

    timeoutRef.current = window.setTimeout(() => {
      setStatus((prev) => (prev === 'loading' ? 'timeout' : prev));
    }, TIMEOUT_MS);

    return () => {
      if (timeoutRef.current != null) window.clearTimeout(timeoutRef.current);
      if (successRedirectRef.current != null) window.clearTimeout(successRedirectRef.current);
    };
  }, [paymentGatewayPaymentId]);

  return (
    <div className="flex flex-1 flex-col items-center justify-center space-y-4 p-6 text-center">
      {status === 'loading' && (
        <>
          <Loader2 className="size-12 animate-spin text-primary" />
          <p className="text-lg font-medium">Processing your payment...</p>
          <p className="text-sm text-muted-foreground">
            Please wait while we confirm your payment.
          </p>
        </>
      )}

      {status === 'success' && (
        <>
          <CheckCircle2 className="size-12 text-primary" />
          <p className="text-lg font-medium">Payment successful!</p>
          <p className="text-sm text-muted-foreground">Redirecting...</p>
        </>
      )}

      {status === 'timeout' && (
        <>
          <AlertCircle className="size-12 text-primary" />
          <p className="text-lg font-medium">Still processing your payment</p>
          <p className="text-sm text-muted-foreground">
            Your payment is still being confirmed. Please check again in a moment.
          </p>
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-center">
            <ActionButton
              onClick={() => {
                setStatus('loading');
                timeoutRef.current = window.setTimeout(() => {
                  setStatus((prev) => (prev === 'loading' ? 'timeout' : prev));
                }, TIMEOUT_MS);
              }}
            >
              Check again
            </ActionButton>
            <ActionButton variant="outline" onClick={onBack}>
              Back
            </ActionButton>
          </div>
        </>
      )}

      {status === 'failed' && (
        <>
          <XCircle className="size-12 text-destructive" />
          <p className="text-lg font-medium">Payment failed</p>
          <p className="text-sm text-muted-foreground">
            Payment session data not found. Please try again.
          </p>
          <ActionButton onClick={onBack}>Back</ActionButton>
        </>
      )}
    </div>
  );
};
